import React, { FC, useEffect, useRef, useState } from 'react';

import { Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Link } from 'react-router-dom';
import { scroller } from 'react-scroll';

import ChevronLeftIcon from '~/assets/icons/ChevronLeftIcon';
import ReminderIcon from '~/assets/icons/ReminderIcon';
import Blink from '~/components/Blink/Blink';
import CustomButton from '~/components/UI/CustomButton/CustomButton';
import {
  SMALL_MARGIN_PX,
  SMALL_RADIUS_PX,
  SUBMIDDLE_MARGIN,
  SUBMIDDLE_MARGIN_PX,
  TINY_MARGIN_PX,
} from '~/theme';
import { isElementInViewport } from '~/utils/positionOnScreen/isElementInViewport';

const WRAPPER_ID = 'asl-banner';

interface IAslBanner {
  aslLink: string;
  aslTitle?: string;
  videoTitle?: string;
  buttonLink: string;
  buttonTitle: string;
  withoutAnimation?: boolean;
}

const AslBanner: FC<IAslBanner> = ({
  aslLink,
  aslTitle,
  videoTitle,
  buttonLink,
  buttonTitle,
  withoutAnimation = false,
}) => {
  const [blinking, setBlinking] = useState(false);
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (withoutAnimation) return;
    const isInViewport = isElementInViewport(wrapperRef.current, 100);
    const { height = 0 } = wrapperRef?.current?.getBoundingClientRect() || {};

    !isInViewport &&
      scroller.scrollTo(WRAPPER_ID, {
        duration: 1000,
        delay: 150,
        smooth: 'easeInOutQuart',
        offset: -window.innerHeight + height + SUBMIDDLE_MARGIN,
      });
    setBlinking(true);
  }, [withoutAnimation]);

  return (
    <Wrapper ref={wrapperRef} blinking={blinking} data-testid="aslBanner" id={WRAPPER_ID}>
      <StyledReminderIcon />
      <Info>
        {aslTitle && (
          <StyledLink data-testid="aslLink" to={aslLink}>
            <StyledChevronLeftIcon />
            <LinkTitle>{aslTitle}</LinkTitle>
          </StyledLink>
        )}
        {videoTitle && (
          <InfoTitle data-testid="nextVideoTitle" variant="h4">
            {videoTitle}
          </InfoTitle>
        )}
      </Info>
      <LinkWrapper data-testid="aslNextButton" to={buttonLink}>
        <CustomButton>{buttonTitle}</CustomButton>
      </LinkWrapper>
    </Wrapper>
  );
};

export default AslBanner;

const Wrapper = styled(Blink)`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  min-height: 90px;
  padding: ${SMALL_MARGIN_PX} ${SMALL_MARGIN_PX} ${SMALL_MARGIN_PX} ${SUBMIDDLE_MARGIN_PX};
  margin-bottom: ${SUBMIDDLE_MARGIN_PX};
  border-radius: ${SMALL_RADIUS_PX};
  background: ${({ theme }) => theme.palette.common.card.lighterAccent};
  box-shadow: ${({ theme }) => theme.palette.common.card.shadowValue};

  ${({ theme }) => theme.breakpoints.up('md')} {
    flex-direction: row;
    padding: ${SMALL_MARGIN_PX} ${SUBMIDDLE_MARGIN_PX};
  }

  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 8px;
    height: 100%;
    background: ${({ theme }) => theme.palette.common.accent};
    border-top-left-radius: inherit;
    border-bottom-left-radius: inherit;
  }
`;
const Info = styled('div')`
  margin-bottom: ${SMALL_MARGIN_PX};
  margin-right: auto;

  ${({ theme }) => theme.breakpoints.up('md')} {
    margin-bottom: 0;
  }
`;
const StyledReminderIcon = styled(ReminderIcon)`
  display: none;
  width: 44px;
  height: 44px;
  min-width: 44px;
  margin-right: ${SMALL_MARGIN_PX};

  ${({ theme }) => theme.breakpoints.up('md')} {
    display: block;
  }

  & path {
    stroke: currentColor;
  }
`;
const StyledChevronLeftIcon = styled(ChevronLeftIcon)`
  width: 12px;
  height: 12px;
  min-width: 12px;
  margin-right: 4px;
  color: ${({ theme }) => theme.palette.common.text.label};
`;
const StyledLink = styled(Link)`
  display: flex;
  align-items: center;
  margin-bottom: ${TINY_MARGIN_PX};
  font-size: 14px;
  color: ${({ theme }) => theme.palette.common.text.label};
`;
const LinkWrapper = styled(Link)`
  display: block;
  width: 100%;

  ${({ theme }) => theme.breakpoints.up('md')} {
    width: unset;
    margin-left: ${SMALL_MARGIN_PX};
  }
`;
const InfoTitle = styled(Typography)`
  && {
    font-size: 16px;
    font-weight: 400;

    ${({ theme }) => theme.breakpoints.up('md')} {
      font-size: 18px;
    }
  }
`;
const LinkTitle = styled('span')`
  position: relative;
  top: -1px;
`;
